import React from 'react';
import { ArrowRight, Search, Lightbulb, Code, BarChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockServices } from '../mock';

const Services = () => {
  const process = [
    {
      number: '01',
      title: 'Discover',
      description: 'We dive deep into your business, audience, and goals. Through workshops and data analysis, we identify opportunities and challenges.',
      icon: Search
    },
    {
      number: '02',
      title: 'Strategise',
      description: 'Armed with insights, we craft a custom roadmap. Every tactic is chosen for maximum impact on your specific KPIs.',
      icon: Lightbulb
    },
    {
      number: '03',
      title: 'Build',
      description: 'Our team executes with precision — designing, developing, and deploying solutions that are both beautiful and effective.',
      icon: Code
    },
    {
      number: '04',
      title: 'Optimise',
      description: 'Launch is just the beginning. We continuously monitor, test, and refine to ensure sustained growth and improving ROI.',
      icon: BarChart
    }
  ];

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="py-20" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)', borderBottom: '1px solid rgba(5, 46, 108, 0.12)' }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-['Space_Grotesk'] font-bold text-5xl md:text-6xl text-[#052E6C] mb-6">
            20+ services to fuel your growth
          </h1>
          <p className="font-['Inter'] text-xl leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
            From brand identity to cyber security, we offer end-to-end solutions that transform businesses. Every service is designed to integrate seamlessly and drive measurable results.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockServices.map((service) => {
              const card = (
                <Card 
                  key={service.id}
                  className="group transition-all duration-300 hover:shadow-lg cursor-pointer relative overflow-hidden h-full"
                  style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#11AAB6] to-[#052E6C] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top" />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <span className="font-['Space_Grotesk'] font-bold text-2xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]">
                        {String(service.id).padStart(2, '0')}
                      </span>
                      <ArrowRight className="text-[#11AAB6] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" size={20} />
                    </div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-lg text-[#052E6C] mb-3 leading-snug">
                      {service.title}
                    </h3>
                    <p className="font-['Inter'] text-sm leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                      {service.description}
                    </p>
                    {service.link && (
                      <span className="inline-block mt-4 font-['Inter'] text-sm font-semibold text-[#11AAB6]">
                        Learn more →
                      </span>
                    )}
                  </CardContent>
                </Card>
              );
              return service.link ? (
                <Link key={service.id} to={service.link} data-testid={`service-card-link-${service.id}`}>
                  {card}
                </Link>
              ) : card;
            })}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-4">
              How we work
            </h2>
            <p className="font-['Inter'] text-lg max-w-2xl mx-auto" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
              Our proven 4-step process ensures every project delivers exceptional results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <Card className="transition-all duration-300 h-full" style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#11AAB6] to-[#052E6C] flex items-center justify-center mb-6">
                      <step.icon className="text-white" size={24} />
                    </div>
                    <span 
                      className="font-['Space_Grotesk'] font-bold text-4xl absolute top-4 right-6"
                      style={{ color: 'rgba(17, 170, 182, 0.12)' }}
                    >
                      {step.number}
                    </span>
                    <h3 className="font-['Space_Grotesk'] font-bold text-xl text-[#052E6C] mb-3">
                      {step.title}
                    </h3>
                    <p className="font-['Inter'] text-sm leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
                {/* Connector Arrow */}
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="text-[#11AAB6]" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-[#052E6C] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-white mb-6">
            Ready to get started?
          </h2>
          <p className="font-['Inter'] text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Let's discuss which services will drive the biggest impact for your business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              asChild
              className="bg-white text-[#052E6C] hover:bg-white/90 px-8 py-6 text-lg rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            >
              <a href="https://booking.impactechdigital.com" target="_blank" rel="noopener noreferrer">
                Book Free Consultation
                <ArrowRight className="ml-2 inline-block group-hover:translate-x-1 transition-transform" size={20} />
              </a>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white px-8 py-6 text-lg rounded-full font-['Inter'] font-medium transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Link to="/contact">
                Get in Touch
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Services;
